// Copyright 2024 praveen.konathala
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Table,
  Tooltip,
  Typography,
} from '@mui/material';
import { LargeGraph } from 'src/components/LargeGraph/LargeGraph';
import TileView from 'src/components/TileView/TileView';
import { useSelector } from 'react-redux';
import {
  filtersData,
  selectisExportToCsv,
  selectSelectedFilter,
  selectShowTable,
  selectShowTrendLine,
} from 'src/app/selectors/filterSelectors';
import Papa from 'papaparse'; // Import papaparse for CSV generation
import { selectReport, selectTile } from 'src/app/selectors/appDataSelectors';
import { AppConfig } from 'src/config/AppConfig';
import BasicTable from 'src/components/basicTable/BasicTable';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const Dashboard = () => {
  const selectedMenu = useSelector(selectReport);
  const selectedTileReport = useSelector(selectTile);
  const isShowTable = useSelector(selectShowTable);
  const isExportToCSV = useSelector(selectisExportToCsv);
  const { channel, store } = useSelector(selectSelectedFilter);
  const isShowTrendLine = useSelector(selectShowTrendLine);

  const filterState = useSelector(filtersData);

  const selectedReportData = filterState[selectedMenu];
  const reports = selectedReportData ? Object.keys(selectedReportData) : [];

  const graphData =
    AppConfig.tileMapping[selectedMenu] &&
    selectedReportData &&
    selectedTileReport in selectedReportData
      ? selectedReportData[selectedTileReport][
          AppConfig.tileMapping[selectedMenu][selectedTileReport].resultKey
        ]
      : [];
  const selectedStores = store && store.length > 0 ? store?.split(',') : [];
  const selectedChannels =
    channel && channel.length > 0 ? channel?.split(',') : [];
  let graphMultiDataKeys = selectedChannels;
  if (selectedStores.length > 1) {
    graphMultiDataKeys = selectedStores;
  }
  // Convert graph data into CSV format
  const convertToCSV = (data: any[]) => {
    const keys = Object.keys(data[0]);
    const csvData = data.map(row => keys.map(key => row[key]).join(","));
    return [keys.join(",")].concat(csvData).join("\n");
  };
  let graphDataForTable = [];
  // Function to download CSV
  const downloadCSV = () => {
    if (!graphDataForTable || graphDataForTable.length === 0) return;
    const csv = convertToCSV(graphDataForTable); // Assuming the data you want to download is inside 'data'
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `swyft_report_${selectedTileReport}.csv`; // Set the file name
    link.click();
    window.URL.revokeObjectURL(url); // Clean up after download
  };
  if (
    graphData.length > 0 &&
    AppConfig.tileMapping[selectedMenu][selectedTileReport].graphType !=
      'funnel'
  ) {
    graphDataForTable = graphData[0].data.map((item, index) => ({
      y: item.y | item.value | item.Sessions,
      value: item.value | item.Transactions,
      ...item,
      y_mavg: isShowTrendLine ? graphData[1]?.mavgData[index]?.y_mavg : 0,
    }));
  }
  useEffect(() => {
    if (isExportToCSV) {
      downloadCSV();
    }
  }, [isExportToCSV]);

  let data = [];

  if (
    AppConfig.tileMapping[selectedMenu] &&
    selectedReportData &&
    selectedReportData['new_customers'] &&
    selectedReportData['new_customers'].results &&
    selectedReportData['new_customers'].results[0]
  ) {
    data = [
      {
        name: 'New Customer',
        value:
          selectedReportData['new_customers'].results[0][
            AppConfig.tileMapping[selectedMenu]['new_customers'].sum_key
          ],
      },
      {
        name: 'Repeating Customer',
        value:
          selectedReportData['returning_customers'].results[0][
            AppConfig.tileMapping[selectedMenu]['returning_customers'].sum_key
          ],
      },
    ];
  }
  // const COLORS = ['#FFBB28', '#FF8042'];
  const COLORS = ['#FF8042', '#FFBB28'];
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="p-1">
      {/* Top half: Filters and Chart */}
      <div className="mb-2">
        {/* Placeholder for the Revenue Chart */}
        <Card className="mb-2">
          <CardContent sx={{ padding: '0px','&:last-child': { paddingBottom: '0px !important' } }}>
            {AppConfig.tileMapping[selectedMenu] &&
              AppConfig.tileMapping[selectedMenu][selectedTileReport] && (
                <Typography variant="h5" className="mb-2" sx= {{fontSize : '1.25rem'}}>
                  {localStorage.getItem('selectedReportTileTitle') ||
                    AppConfig.tileMapping[selectedMenu][selectedTileReport]
                      .title_key}
                </Typography>
              )}
            <Box className="h-64 ">
              {/* Placeholder for the actual chart */}
              {graphData && graphData.length > 0 && graphData[0]?.data && (
                <LargeGraph
                  graphData={graphData}
                  config={
                    AppConfig.tileMapping[selectedMenu][selectedTileReport]
                  }
                />
              )}
              {graphData &&
                graphData.length > 0 &&
                AppConfig.tileMapping[selectedMenu][selectedTileReport]
                  .graphType == 'funnel' && (
                  <LargeGraph
                    graphData={graphData}
                    config={
                      AppConfig.tileMapping[selectedMenu][selectedTileReport]
                    }
                  />
                )}
            </Box>
          </CardContent>
        </Card>
      </div>
      {isShowTable &&
        graphData &&
        graphData.length > 0 &&
        graphData[0]?.data && (
          // <Box sx={{ overflow: 'visible', width: '0', height:'125px' }}>
          <Box sx={{ overflowX: 'hidden', width: '100%' }}>
            <Card className="mb-2">
              <CardContent>
                <BasicTable
                  data={graphDataForTable}
                  config={
                    AppConfig.tileMapping[selectedMenu][selectedTileReport]
                  }
                  graphMultiDataKeys={graphMultiDataKeys}
                />
              </CardContent>
            </Card>
            {/* </Box> */}
          </Box>
        )}

      {/* Bottom half: Metrics and Summary */}
      <div className="flex flex-wrap w-full gap-4">
        {/* 80% section for 8 tiles */}
        <div
          className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4"
          style={{ height: 'auto',display: 'grid', alignContent: 'start' }}
        >
          {AppConfig.tileMapping[selectedMenu] &&
            reports.map((item, index) => {
              const tileConfig = AppConfig.tileMapping[selectedMenu][item];
              const valueResult = selectedReportData[item][tileConfig.resultKey]
              const value = valueResult.length > 0 ? valueResult[0][tileConfig.sum_key] : ''
              return (
                <TileView
                  key={index + '--' + item}
                  keyValue={index + '--' + item}
                  graphData={selectedReportData[item][tileConfig.resultKey]}
                  config={tileConfig}
                  isSelected={item === selectedTileReport}
                  dataKey={item}
                  tooltip={tileConfig.toolTip}
                  title={tileConfig.title_key}
                  value={tileConfig.format(
                    tileConfig.graphType === 'funnel'
                      ? ''
                      : value,
                  )}
                />
              );
            })}
        </div>

        {/* 20% section for the summary */}
        <div className="w-full lg:w-1/4 lg:mt-0 mt-4">
          <Card className="lg:h-full" sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
            <CardContent>
              {AppConfig.tileMapping[selectedMenu] && (
                <Typography variant="h6" mb ={2}>
                  {AppConfig.tileMapping[selectedMenu].summaryTitle}
                </Typography>
              )}
              {reports &&
                reports.length > 0 &&
                AppConfig.tileMapping[selectedMenu] &&
                AppConfig.tileMapping[selectedMenu].summary.map((item) => (
                  <Typography key={item} mb={2} display="flex" justifyContent="space-between" pb={1} borderBottom="1px solid #ccc">
                    <span>{AppConfig.summaryMapping[item]}: </span>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {AppConfig.summaryMappingFormat[item](
                        selectedReportData[item].results[0][
                          AppConfig.tileMapping[selectedMenu][item].sum_key
                        ],
                      )}
                    </span>
                  </Typography>
                ))}
              <Box ml={1}>
                <List>
                  {data.map((entry, index) => (
                    <ListItem key={`legend-${index}-`} disableGutters>
                      <Box
                        width={8}
                        height={8}
                        bgcolor={COLORS[index % COLORS.length]}
                        mr={2}
                        borderRadius="50%"
                      />
                      <Typography variant="caption">{entry.name}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
              {
                <ResponsiveContainer
                  width="100%"
                  height={300}
                  style={{ marginTop: '-50px' }}
                >
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              }
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
